import React from 'react';
import { createPortal } from 'react-dom';
import { FaCheckCircle, FaTimesCircle, FaSpinner, FaPaperPlane, FaBan } from 'react-icons/fa';
import '../../styles/SendProgressModal.css';

const SendProgressModal = ({ 
  show, 
  onClose, 
  totalMessages, 
  sentMessages, 
  failedMessages,
  skippedMessages = 0,
  currentRecipient,
  status, // 'sending', 'completed', 'error'
  results
}) => {
  if (!show) return null;

  const progress = totalMessages > 0 ? ((sentMessages + failedMessages + skippedMessages) / totalMessages) * 100 : 0;
  const isCompleted = status === 'completed';
  const successRate = totalMessages > 0 ? ((sentMessages / totalMessages) * 100).toFixed(1) : 0;

  return createPortal(
    <div className="sp-overlay">
      <div className="sp-modal">
        {/* Header */}
        <div className="sp-header">
          <div className="sp-header-icon">
            {status === 'sending' && <FaSpinner className="sp-spin" />}
            {status === 'completed' && <FaCheckCircle />}
          </div>
          <h2>{status === 'sending' ? 'Sending Notices...' : 'Sending Complete'}</h2>
        </div>

        {/* Body */}
        <div className="sp-body">
          {/* Progress */}
          <div className="sp-progress-row">
            <span className="sp-progress-count">{sentMessages + failedMessages} / {totalMessages}</span>
            <span className="sp-progress-pct">{progress.toFixed(0)}%</span>
          </div>
          <div className="sp-bar-track">
            <div className="sp-bar-fill" style={{ width: `${progress}%` }} />
          </div>

          {/* Current batch */}
          {status === 'sending' && currentRecipient && (
            <div className="sp-current">
              <FaPaperPlane className="sp-current-icon" />
              <span>Sending to: <strong>{currentRecipient.name}</strong> ({currentRecipient.phone})</span>
            </div>
          )}

          {/* Stat cards */}
          <div className="sp-stats">
            <div className="sp-stat sp-stat-success">
              <FaCheckCircle className="sp-stat-icon" />
              <div>
                <div className="sp-stat-val">{sentMessages}</div>
                <div className="sp-stat-lbl">Successful</div>
              </div>
            </div>
            {skippedMessages > 0 && (
              <div className="sp-stat sp-stat-skip">
                <FaBan className="sp-stat-icon" />
                <div>
                  <div className="sp-stat-val">{skippedMessages}</div>
                  <div className="sp-stat-lbl">Skipped</div>
                </div>
              </div>
            )}
            <div className="sp-stat sp-stat-fail">
              <FaTimesCircle className="sp-stat-icon" />
              <div>
                <div className="sp-stat-val">{failedMessages}</div>
                <div className="sp-stat-lbl">Failed</div>
              </div>
            </div>
            <div className="sp-stat sp-stat-total">
              <div>
                <div className="sp-stat-val">{totalMessages}</div>
                <div className="sp-stat-lbl">Total</div>
              </div>
            </div>
          </div>

          {/* Success rate */}
          {isCompleted && (
            <div className="sp-rate">
              <span>Success Rate</span>
              <span className={`sp-rate-val ${successRate >= 90 ? 'high' : successRate >= 50 ? 'mid' : 'low'}`}>
                {successRate}%
              </span>
            </div>
          )}

          {/* Results */}
          {isCompleted && results && (
            <div className="sp-results">
              <h3 className="sp-results-title">Detailed Results</h3>
              <div className="sp-results-list">
                {results.successful && results.successful.length > 0 && (
                  <details className="sp-group sp-group-ok">
                    <summary>Successful ({results.successful.length})</summary>
                    <ul>
                      {results.successful.map((r, i) => (
                        <li key={i}>
                          <span className="sp-r-name">{r.recipient}</span>
                          <span className="sp-r-phone">{r.phone}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {results.skipped && results.skipped.length > 0 && (
                  <details className="sp-group sp-group-skip">
                    <summary>Skipped — Already sent this month ({results.skipped.length})</summary>
                    <ul>
                      {results.skipped.map((r, i) => (
                        <li key={i}>
                          <span className="sp-r-name">{r.recipient}</span>
                          <span className="sp-r-phone">{r.rno}</span>
                          <span className="sp-r-reason">{r.reason || 'Already sent this month'}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {results.failed && results.failed.length > 0 && (
                  <details className="sp-group sp-group-err" open>
                    <summary>Failed ({results.failed.length})</summary>
                    <ul>
                      {results.failed.map((r, i) => (
                        <li key={i}>
                          <span className="sp-r-name">{r.recipient}</span>
                          <span className="sp-r-phone">{r.phone}</span>
                          <span className="sp-r-err">
                            {typeof r.error === 'string' ? r.error : r.error?.message || r.error || 'Unknown error'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sp-footer">
          {isCompleted ? (
            <button className="sp-close-btn" onClick={onClose}>Close</button>
          ) : (
            <p className="sp-note">Please wait while notices are being sent...</p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SendProgressModal;
